import dayjs from 'dayjs';
import { useCallback } from 'react';
import {
  Avatar,
  isMobile,
  FlexLayout,
  TrackScroller,
  AvatarPlaceholderType,
} from '@gv/triage-components';

import { AppointmentTime } from 'types';

import * as Styles from './styles';
import { HospitalCardProps } from './types';

const convertMillisecondsToTime = (milliseconds: number): string =>
  dayjs().startOf('day').add(milliseconds, 'milliseconds').format('h:mm A');

/**
 * HospitalCard component displays hospital information with appointment time slider.
 */
export const HospitalCard = ({
  hospital,
  distance,
  withSlider,
  onButtonClick,
  onAvatarClick,
}: HospitalCardProps) => {
  const mobile = isMobile();

  const { name, logo, address, appointments } = hospital || {};

  const getTimeButton = useCallback(
    ({ id, end, start }: AppointmentTime) => (
      <Styles.Button
        fullWidth={!mobile}
        key={`${end}-${start}`}
        onClick={() => onButtonClick?.(id)}
        text={`${convertMillisecondsToTime(start)} - ${convertMillisecondsToTime(end)}`}
      />
    ),
    [mobile, onButtonClick]
  );

  return (
    <Styles.Container>
      <FlexLayout
        justifyContent="space-between"
        flexDirection={mobile ? 'column' : 'row'}
      >
        <FlexLayout gap={8}>
          <Avatar
            square
            size={48}
            url={logo}
            userName={name}
            onClick={onAvatarClick}
            placeholder={AvatarPlaceholderType.Hospital}
          />
          <FlexLayout flexDirection="column">
            <h3 className="semibold">{name}</h3>
            <p>{address}</p>
          </FlexLayout>
        </FlexLayout>

        {distance && (
          <FlexLayout alignItems="flex-end">
            <p className="semibold">{distance} mi away</p>
          </FlexLayout>
        )}
      </FlexLayout>

      <TrackScroller<AppointmentTime>
        data={appointments[0].time}
        renderFunction={getTimeButton}
        horizontalScroll={!withSlider || mobile}
      />
    </Styles.Container>
  );
};
